/**
 * Payment Hooks
 *
 * React hooks for payment operations using TanStack Query.
 * Provides data fetching, mutations, and caching for payment-related operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    getStripeConfig,
    createPaymentIntent,
    reconcileCheckoutPayment,
    getCheckoutPaymentStatus,
    createCustomPaymentIntent,
    createSetupIntent,
    getPaymentMethods,
    setDefaultPaymentMethod,
    deletePaymentMethod,
    cancelSubscription,
    getCancellationPreview,
} from '@/api/payments';
import {
    CreatePaymentIntentRequest,
    CustomPaymentIntentRequest,
    PaymentIntentResponse,
    SetupIntentResponse,
    CheckoutPaymentStatusResponse,
} from '@/types/payments';
import { CancelSubscriptionApiResponse } from '@/types/reservations';

// =============================================================================
// Query Keys
// =============================================================================

export const paymentKeys = {
    all: ['payments'] as const,
    config: () => [...paymentKeys.all, 'config'] as const,
    methods: () => [...paymentKeys.all, 'methods'] as const,
    checkoutStatus: (paymentIntentId?: string | null) => [...paymentKeys.all, 'checkout-status', paymentIntentId] as const,
};

// =============================================================================
// Configuration
// =============================================================================

/**
 * Hook to get the PLATFORM Stripe configuration (publishable key).
 *
 * Multi-tenant note: customer charges run on the Stripe account of the booking's
 * location, and every payment response (PaymentIntent / SetupIntent) carries the
 * publishable key of the account it was created on — prefer that key. This config
 * key is only a fallback for flows that genuinely run on the platform account.
 * Pass `enabled: false` to skip the fetch entirely when a key is already known.
 */
export const useStripeConfig = (enabled: boolean = true) => {
    return useQuery({
        queryKey: paymentKeys.config(),
        queryFn: getStripeConfig,
        staleTime: Infinity, // Config doesn't change
        gcTime: Infinity,
        enabled,
    });
};

// =============================================================================
// Payment Intents
// =============================================================================

/**
 * Hook to create a payment intent for cart checkout.
 */
export const useCreatePaymentIntent = () => {
    return useMutation<
        PaymentIntentResponse,
        Error,
        { request?: CreatePaymentIntentRequest; sessionId?: string }
    >({
        mutationFn: ({ request, sessionId }) => createPaymentIntent(request, sessionId),
    });
};

export const useReconcileCheckoutPayment = () => {
    return useMutation<
        CheckoutPaymentStatusResponse,
        Error,
        { paymentIntentId: string; sessionId?: string }
    >({
        mutationFn: ({ paymentIntentId, sessionId }) =>
            reconcileCheckoutPayment(paymentIntentId, sessionId),
    });
};

export const useCheckoutPaymentStatus = (
    paymentIntentId: string | null,
    sessionId?: string,
    enabled = true
) => {
    return useQuery({
        queryKey: paymentKeys.checkoutStatus(paymentIntentId),
        queryFn: () => getCheckoutPaymentStatus(paymentIntentId!, sessionId),
        enabled: enabled && !!paymentIntentId,
        refetchInterval: 1000,
        refetchOnWindowFocus: false,
        retry: false,
    });
};

/**
 * Hook to create a custom payment intent for specific amounts.
 */
export const useCreateCustomPaymentIntent = () => {
    return useMutation<PaymentIntentResponse, Error, CustomPaymentIntentRequest>({
        mutationFn: createCustomPaymentIntent,
    });
};

// =============================================================================
// Setup Intents
// =============================================================================

/**
 * Hook to create a setup intent for saving cards.
 *
 * Pass an optional `bookingId` to create the SetupIntent on a specific monthly
 * rental's location Stripe account (GAP2); omit it for a generic save-card.
 */
export const useCreateSetupIntent = () => {
    return useMutation<SetupIntentResponse, Error, string | void>({
        mutationFn: (bookingId) => createSetupIntent(bookingId || undefined),
    });
};

// =============================================================================
// Payment Methods
// =============================================================================

/**
 * Hook to get customer's saved payment methods.
 */
export const usePaymentMethods = (enabled = true) => {
    return useQuery({
        queryKey: paymentKeys.methods(),
        queryFn: getPaymentMethods,
        enabled,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

/**
 * Hook to set a payment method as default.
 */
export const useSetDefaultPaymentMethod = () => {
    const queryClient = useQueryClient();

    return useMutation<{ success: boolean }, Error, string>({
        mutationFn: setDefaultPaymentMethod,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: paymentKeys.methods() });
        },
    });
};

/**
 * Hook to delete a saved payment method.
 */
export const useDeletePaymentMethod = () => {
    const queryClient = useQueryClient();

    return useMutation<{ success: boolean }, Error, string>({
        mutationFn: deletePaymentMethod,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: paymentKeys.methods() });
        },
    });
};

// =============================================================================
// Subscription Management
// =============================================================================

/**
 * Hook to get a preview of subscription cancellation charges.
 * Fetches tier fallback details before the user confirms cancellation.
 */
export const useCancellationPreview = (customerBookingId: string | null, enabled = false) => {
    return useQuery({
        queryKey: ['cancellation-preview', customerBookingId],
        queryFn: () => getCancellationPreview(customerBookingId!),
        enabled: enabled && !!customerBookingId,
        staleTime: 1000 * 30, // 30 seconds - prices may change
        refetchOnWindowFocus: false,
    });
};

/**
 * Hook to cancel a subscription rental.
 * Returns tier fallback penalty details for early termination.
 */
export const useCancelSubscription = () => {
    const queryClient = useQueryClient();

    return useMutation<
        CancelSubscriptionApiResponse,
        Error,
        { customerBookingId: string; cancelImmediately?: boolean }
    >({
        mutationFn: ({ customerBookingId, cancelImmediately }) =>
            cancelSubscription(customerBookingId, cancelImmediately),
        onSuccess: () => {
            // Invalidate reservations cache to reflect the cancellation
            queryClient.invalidateQueries({ queryKey: ['reservations'] });
        },
    });
};
